import React, { useEffect, useState } from "react";
import { Route, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import firebase from "firebase/app";
import "firebase/firestore";

import Login from "views/Login.jsx";
import Registration from "views/Registration.jsx";
import SubscriptionPage from "../views/SubscriptionPage";
import PaymentSuccess from "../services/PaymentSuccess";
import App from "layouts/App.jsx";
import Authenticate from "app-components/Authenticate.jsx";

const AuthenticatedApp = Authenticate(App);

const PrivateRoute = ({ component: Component, ...rest }) => {
    const user = useSelector((state) => state.user.user);
    const offlineUser = useSelector((state) => state.user.offlineUser);
    const offlineAuthDone = useSelector((state) => state.user.offlineAuthDone);

    const [isChecking, setIsChecking] = useState(true);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [fullUserData, setFullUserData] = useState(null);

    const isAuthenticated = user || (offlineUser && offlineAuthDone);


    const fetchUserDataFromFirestore = async (userId) => {
        try {
            const doc = await firebase.firestore().collection("users").doc(userId).get();
            if (doc.exists) return doc.data();
            return null;
        } catch (err) {
            return null;
        }
    };

    const hasLocalSubscription = (subscription) => {
        if (!subscription) return false;

        const isStatusActive =
            subscription.status?.toLowerCase() === "actif" ||
            subscription.status?.toLowerCase() === "active";

        let isNotExpired = true;
        if (subscription.endDate?.toDate) {
            const expiryDate = subscription.endDate.toDate();
            isNotExpired = expiryDate > new Date();
        }

        return isStatusActive && isNotExpired;
    };

    useEffect(() => {
        let isMounted = true;

        const verifyAccess = async () => {
            if (!isAuthenticated) {
                if (isMounted) setIsChecking(false);
                return;
            }

            const userId = user?.uid || offlineUser?.uid || offlineUser?.id;
            if (!userId) {
                if (isMounted) setIsChecking(false);
                return;
            }

            // Si l'utilisateur est en mode hors ligne et a des données locales, l'autoriser
            if (offlineUser && offlineAuthDone && !user) {
                if (isMounted) {
                    // Pour les utilisateurs hors ligne, utiliser les données locales du profil
                    setFullUserData(offlineUser.profile || offlineUser);
                    setUserRole(offlineUser.profile?.role || offlineUser.role || 'user');
                    setIsSubscribed(true); // Autoriser l'accès hors ligne
                    setIsChecking(false);
                }
                return;
            }

            try {
                const firestoreUser = await fetchUserDataFromFirestore(userId);
                if (!isMounted) return;

                if (!firestoreUser) {
                    setIsSubscribed(false);
                    setIsChecking(false);
                    return;
                }

                setFullUserData(firestoreUser);
                setUserRole(firestoreUser.role);

                if (firestoreUser.role === "admin" || firestoreUser.role === "administrator") {
                    setIsSubscribed(true);
                } else if (hasLocalSubscription(firestoreUser.subscription)) {
                    setIsSubscribed(true);
                } else {
                    setIsSubscribed(false);
                }
            } catch (error) {
                if (isMounted) {
                    // Si erreur réseau et utilisateur authentifié localement, autoriser l'accès
                    if (offlineUser && offlineAuthDone) {
                        setIsSubscribed(true);
                    } else {
                        setIsSubscribed(false);
                    }
                }
            } finally {
                if (isMounted) setIsChecking(false);
            }
        };

        verifyAccess();

        return () => {
            isMounted = false;
        };
    }, [user, offlineUser, offlineAuthDone, isAuthenticated]);

    if (!isAuthenticated) {
        return <Redirect to="/" />;
    }
    if (isChecking) {
        return <div style={{ textAlign: "center", marginTop: "3rem" }}>Chargement...</div>;
    }
    if (!isSubscribed) {
        return <Redirect to="/abonnement" />;
    }


    return (
        <Route {...rest} render={(props) => <Component {...props} />} />
    );
};

const indexRoutes = [
    { path: "/registration", component: Registration },
    { path: "/", exact: true, component: Login },
    { path: "/abonnement", exact: true, component: SubscriptionPage },
    { path: "/payment/success", exact: true, component: PaymentSuccess },
    {
        path: "/app",
        component: (props) => <PrivateRoute component={AuthenticatedApp} {...props} />,
    },
];

export default indexRoutes;
